import React, { useContext, useEffect, useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { intersection } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { apiEndPoints } from '../../../common/api.endPoints';
import { FlexCol, FlexRow, Select } from '../../../common/components/v2';
import { Resource } from '../../../common/constants/resource';
import {
  getCarrierRole,
  isNonCarrierRolePresent,
  NoraPermissionV2,
  resolveCarrierRole,
  RoleDetailedLifeCycleMap,
  Roles,
} from '../../../common/constants/roles';
import ElementLocators from '../../../common/ElementLocators';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { useAccess } from '../../../common/hooks/useAccess';
import { HttpService } from '../../../common/http.service';
import { IKeyValMap } from '../../../common/interfaces/IKeyValMap';
import { SnackbarContext } from '../../layout/SnackbarContext';
import { AuthContext } from '../../login/AuthWrapper';
import { reassignApplication } from '../detailSlice';
import { IFilterItem } from '../interfaces/IFilterItem';
import styles from './styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const Reassign = ({ applicationId }: { applicationId: string }) => {
  const {
    underwriterId,
    underwriterName,
    carrierUnderwriterId,
    carrierUnderwriterName,
    caseManagerId,
    caseManagerName,
    uwxUserId,
    uwxUserName,
    referralUserId,
    referralUserName,
    reassignmentFailed,
    reassignmentError,
    reassignmentErrorType,
    detailedLifecycle,
  }: {
    underwriterId: number;
    underwriterName: string;
    carrierUnderwriterId: number;
    carrierUnderwriterName: string;
    caseManagerId: number;
    caseManagerName: string;
    uwxUserId: number;
    uwxUserName: string;
    referralUserId: number;
    referralUserName: string;
    reassignmentFailed: boolean;
    reassignmentError: string | undefined;
    reassignmentErrorType: string | undefined;
    applicationLifecycleV2: string | undefined;
    detailedLifecycle: string | undefined;
  } = useSelector((state: RootState) => {
    return {
      underwriterId: state.application.underwriterId,
      underwriterName: state.application.underwriterName,
      carrierUnderwriterId: state.application.carrierUnderwriterId,
      carrierUnderwriterName: state.application.carrierUnderwriterName,
      caseManagerId: state.application.caseManagerId,
      caseManagerName: state.application.caseManagerName,
      uwxUserId: state.application.uwxUserId,
      uwxUserName: state.application.uwxUserName,
      referralUserId: state.application.referralUserId,
      referralUserName: state.application.referralUserName,
      reassignmentFailed: state.application.reassignmentFailed,
      reassignmentError: state.application.reassignmentError,
      reassignmentErrorType: state.application.reassignmentErrorType,
      applicationLifecycleV2: state.application.applicationLifecycleV2,
      detailedLifecycle: state.application.applicationDetailedLifecycle,
    };
  });

  const [underwriters, setUnderwriters] = useState({
    users: [] as IFilterItem[],
  });

  const [carrierUnderwriters, setCarrierUnderwriters] = useState({
    users: [] as IFilterItem[],
  });

  const [caseManagers, setCaseManagers] = useState({
    users: [] as IFilterItem[],
  });

  const [uwxUsers, setuwxUsers] = useState({
    users: [] as IFilterItem[],
  });

  const [referralUsers, setReferralUsers] = useState({
    users: [] as IFilterItem[],
  });

  const [currentCM, setCurrentCM] = useState({
    value: caseManagerId,
    label: caseManagerName,
  } as IFilterItem);

  const [currentUW, setCurrentUW] = useState({
    value: underwriterId,
    label: underwriterName,
  } as IFilterItem);

  // Current Carrier Underwriter
  const [currentCUW, setCurrentCUW] = useState({
    value: carrierUnderwriterId,
    label: carrierUnderwriterName,
  } as IFilterItem);

  const [currentUWX, setCurrentUWX] = useState({
    value: uwxUserId,
    label: uwxUserName,
  } as IFilterItem);

  const [currentReferral, setCurrentReferral] = useState({
    value: referralUserId,
    label: referralUserName,
  } as IFilterItem);

  // 'idle' or 'loading' or 'success' or 'error'
  const [fetchStatus, setFetchStatus] = useState('idle');
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const auth = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const isNonCarrierRole = isNonCarrierRolePresent(auth?.roles);
  const isCarrierRole = getCarrierRole(auth?.roles);
const resolvedCarrierRole = resolveCarrierRole(auth.roles, detailedLifecycle);
  const canUserAssignForCurrentLifecycle = resolvedCarrierRole
    ? detailedLifecycle === RoleDetailedLifeCycleMap.get(resolvedCarrierRole)
    : false;

  const canReassignUnderwriter = useAccess({
    permission: 'REASSIGN_UNDERWRITER',
    legacyCheck: roles => {
      return roles.some(role =>
        [
          Roles.ADMIN,
          Roles.CASEMANAGER,
          Roles.CHIEF_UNDERWRITER,
          Roles.UW_UNDERWRITING_MANAGER,
        ].includes(role),
      );
    },
  });

  const canReassignCaseManager = useAccess({
    permission: NoraPermissionV2.REASSIGN_CASE_MANAGER,
  });

  const dispatch = useDispatch<AppDispatch>();

  // fetch underwriter
  const loadUnderwriter = async () => {
    const data: IKeyValMap = await HttpService.getInstance().get(
      apiEndPoints.underwriters,
    );
    const items = data.parsedBody ?? [];
    const users: IFilterItem[] = items.map((item: any) => {
      return { value: item.id, label: item.displayName } as IFilterItem;
    });

    if (!underwriterId) {
      const currentUnderWriter = {
        value: -1,
        label: `Not Assigned`,
      };
      users.push(currentUnderWriter);
      setCurrentUW(currentUnderWriter);
    } else {
      let currentUnderWriter = users.find(
        (user: IFilterItem) => user.value === currentUW.value,
      );
      if (!currentUnderWriter) {
        currentUnderWriter = {
          value: currentUW.value,
          label: `${currentUW.label}${Resource.DEFUNCT}`,
        };
        users.push(currentUnderWriter);
        setCurrentUW(currentUnderWriter);
      }
    }
    setUnderwriters({
      users,
    });
  };

  // fetch carrierUnderwriter
  const loadCarrierUnderwriter = async () => {
    const data: IKeyValMap = await HttpService.getInstance().get(
      apiEndPoints.carrierunderwriters,
    );
    let items = data.parsedBody ?? [];

    // Filtering users having roles equivalent to current user role.
    items = items.filter(
      (item: any) => intersection(item.roles, [resolvedCarrierRole]).length > 0,
    );
    const users: IFilterItem[] = canUserAssignForCurrentLifecycle
      ? items.map((item: any) => {
          return { value: item.id, label: item.displayName } as IFilterItem;
        })
      : [];
    if (!carrierUnderwriterId) {
      const currentCarrierUnderwriter = {
        value: -1,
        label: `Not Assigned`,
      };
      users.push(currentCarrierUnderwriter);
      setCurrentCUW(currentCarrierUnderwriter);
    } else {
      let currentCarrierUnderwriter = users.find(
        (user: IFilterItem) => user.value === currentCUW.value,
      );
      if (!currentCarrierUnderwriter) {
        currentCarrierUnderwriter = {
          value: currentCUW.value,
          label: `${currentCUW.label}${Resource.DEFUNCT}`,
        };
        users.push(currentCarrierUnderwriter);
        setCurrentCUW(currentCarrierUnderwriter);
      }
    }
    setCarrierUnderwriters({
      users,
    });
  };

  // fetch case managers
  const loadCaseManager = async () => {
    const data: IKeyValMap = await HttpService.getInstance().get(
      apiEndPoints.caseManagers,
    );

    const items = data.parsedBody ?? [];
    const users = items.map((item: any) => {
      return { value: item.id, label: item.displayName } as IFilterItem;
    });
    if (!caseManagerId) {
      const currentCaseManager = {
        value: -1,
        label: `Not Assigned`,
      };
      users.push(currentCaseManager);
      setCurrentCM(currentCaseManager);
    } else {
      let currentCaseManager = users.find(
        (user: IFilterItem) => user.value === currentCM.value,
      );
      if (!currentCaseManager) {
        currentCaseManager = {
          value: currentCM.value,
          label: `${currentCM.label}${Resource.DEFUNCT}`,
        };
        users.push(currentCaseManager);
        setCurrentCM(currentCaseManager);
      }
    }
    setCaseManagers({
      users,
    });
  };

  // fetch uwx users
  const loadUwxUser = async () => {
    const data: IKeyValMap = await HttpService.getInstance().get(
      apiEndPoints.uwxUsers,
    );

    const items = data.parsedBody ?? [];
    let users: IFilterItem[] = [];

    try {
      users = items.map((item: any) => {
        return { value: item.id, label: item.displayName } as IFilterItem;
      });
    } catch (e) {
      // To help investigate UW-906. Remove when fixed.
      console.log('loadUwxUser data', data, e);
      throw e;
    }

    if (!uwxUserId) {
      const currentUwxUser = {
        value: -1,
        label: `${Resource.USER_NOT_ASSIGNED}`,
      };
      users.push(currentUwxUser);
      setCurrentUWX(currentUwxUser);
    } else {
      let currentUwxUser = users.find(
        (user: IFilterItem) => user.value === currentUWX.value,
      );
      if (!currentUwxUser) {
        currentUwxUser = {
          value: currentUWX.value,
          label: `${currentUWX.label}${Resource.DEFUNCT}`,
        };
        users.push(currentUwxUser);
        setCurrentUWX(currentUwxUser);
      }
    }
    setuwxUsers({
      users,
    });
  };

  const loadReferrals = async () => {
    const data: IKeyValMap = await HttpService.getInstance().get(
      apiEndPoints.referrals,
    );
    const items = data.parsedBody ?? [];
    const users: IFilterItem[] = items.map((item: any) => {
      return { value: item.id, label: item.displayName } as IFilterItem;
    });
    if (!referralUserId) {
      const currentReferralUser = {
        value: -1,
        label: `Not Assigned`,
      };
      users.push(currentReferralUser);
      setCurrentReferral(currentReferralUser);
    } else {
      let currentReferralUser = users.find(
        (user: IFilterItem) => user.value === currentReferral.value,
      );
      if (!currentReferralUser) {
        currentReferralUser = {
          value: currentReferral.value,
          label: `${currentReferral.label}${Resource.DEFUNCT}`,
        };
        users.push(currentReferralUser);
        setCurrentReferral(currentReferralUser);
      }
      const unassignUser = {
        value: -1,
        label: `Unassign user`,
      };
      users.push(unassignUser);
    }
    setReferralUsers({
      users,
    });
  };

  useEffect(() => {
    setFetchStatus('loading');
    const loadAll = async () => {
      loadUnderwriter();
      if (isCarrierRole) {
        await loadCarrierUnderwriter();
      }
      if (isNonCarrierRole) {
        await loadCaseManager();
        await loadUwxUser();
        if (!flags.isCaseReferralEnabled) {
          await loadReferrals();
        }
      }
      setFetchStatus('success');
    };
    loadAll();
  }, []);

  useEffect(() => {
    if (reassignmentFailed) {
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: reassignmentErrorType,
          message: reassignmentError,
        },
      ]);
    }
  }, [reassignmentErrorType, reassignmentError, reassignmentFailed]);

  useEffect(() => {
    const newCaseManager = caseManagers.users.find(
      (user: IFilterItem) => user.value === caseManagerId,
    );
    if (newCaseManager) {
      setCurrentCM(newCaseManager);
    }
  }, [caseManagerId]);

  useEffect(() => {
    const newUnderwriter = underwriters.users.find(
      (user: IFilterItem) => user.value === underwriterId,
    );
    if (newUnderwriter) {
      setCurrentUW(newUnderwriter);
    }
  }, [underwriterId]);

  useEffect(() => {
    if (!carrierUnderwriterId) {
      const currentCarrierUnderwriter = {
        value: -1,
        label: `Not Assigned`,
      };
      setCurrentCUW(currentCarrierUnderwriter);
    } else {
      const newCarrierUnderwriter = carrierUnderwriters.users.find(
        (user: IFilterItem) => user.value === carrierUnderwriterId,
      );
      if (newCarrierUnderwriter) {
        setCurrentCUW(newCarrierUnderwriter);
      }
    }
  }, [carrierUnderwriterId]);

  useEffect(() => {
    const newUwxUser = uwxUsers.users.find(
      (user: IFilterItem) => user.value === uwxUserId,
    );
    if (newUwxUser) {
      setCurrentUWX(newUwxUser);
    }
  }, [uwxUserId]);

  useEffect(() => {
    if (!referralUserId) {
      setCurrentReferral({
        value: -1,
        label: `Not Assigned`,
      });
      return;
    }
    const newReferralUser = referralUsers.users.find(
      (user: IFilterItem) => user.value === referralUserId,
    );
    if (newReferralUser) {
      setCurrentReferral(newReferralUser);
    }
  }, [referralUserId]);

  return (
    <div
      className={styles.Title}
      data-label={ElementLocators.LOCATOR_REASSIGN_UNDERWRITER_SECTION}
    >
      <FlexRow className={styles.reassignRow}>
        {isNonCarrierRole && (
          <FlexCol xs={3} className={styles.reassignCol}>
            {fetchStatus === 'success' && (
              <Select
                className={styles.filterUwxUser}
                name="Reassign underwriter"
                value={currentUW}
                options={underwriters.users}
                onChange={(selectedUnderwriter: IFilterItem) => {
                  dispatch(
                    reassignApplication(
                      applicationId,
                      selectedUnderwriter,
                      'UNDERWRITER',
                    ),
                  );
                }}
                title="Underwriter"
                placeholder="Not Assigned"
                isDisabled={
                  (!isNonCarrierRole && isCarrierRole) ||
                  // TODO: re enable role check when we have UW_UNDERWRITER_MANAGER_ROLE
                  !canReassignUnderwriter
                }
              />
            )}
          </FlexCol>
        )}
        {isCarrierRole && (
          <FlexCol xs={3} className={styles.reassignCol}>
            {fetchStatus === 'success' && (
              <Select
                className={styles.filterUwxUser}
                name="Reassign carrierUnderwriter"
                value={currentCUW}
                options={carrierUnderwriters.users}
                onChange={(selectedCarrierUnderwriter: IFilterItem) => {
                  dispatch(
                    reassignApplication(
                      applicationId,
                      selectedCarrierUnderwriter,
                      'CARRIERUNDERWRITER',
                    ),
                  );
                }}
                title="Carrier Underwriter"
                placeholder="Not Assigned"
              />
            )}
          </FlexCol>
        )}
        <FlexCol xs={3} className={styles.reassignCol}>
          {fetchStatus === 'success' && isNonCarrierRole && (
            <Select
              className={styles.filterUwxUser}
              name="Reassign case manager"
              value={currentCM}
              options={caseManagers.users}
              onChange={(selectedCaseManager: IFilterItem) => {
                dispatch(
                  reassignApplication(
                    applicationId,
                    selectedCaseManager,
                    'CASEMANAGER',
                  ),
                );
              }}
              title="Case Manager"
              placeholder="Not Assigned"
              isDisabled={!canReassignCaseManager}
            />
          )}
        </FlexCol>
        {/* Legacy referral dropdown; hidden when Case Referral feature is on */}
        {!flags.isCaseReferralEnabled && (
          <FlexCol xs={3} className={styles.reassignCol}>
            {fetchStatus === 'success' && isNonCarrierRole && (
              <Select
                className={styles.filterUwxUser}
                name="Reassign referral"
                value={currentReferral}
                options={referralUsers.users}
                onChange={(selectedReferral: IFilterItem) => {
                  dispatch(
                    reassignApplication(
                      applicationId,
                      selectedReferral,
                      'REFERRAL',
                    ),
                  );
                }}
                title="Referral"
                placeholder="Not Assigned"
              />
            )}
          </FlexCol>
        )}
      </FlexRow>
    </div>
  );
};
